<template>
  <section class="detail-tour">
    <transition name="partialloader">
      <div v-if="!context" class="pageloader partial">
        <span class="loading-text">{{loadingText}}</span>
      </div>
    </transition>

    <tour
      v-if="context"
      :progress="true"
      :itemsToShow="1.35"
      :centerMode="true"
      :infiniteScroll="false"
      :items="spot_list"
      class="tour-slider"
      :bgimage="context.thumb.url"
      :preview=context.preview
    >
      <template v-slot:cover>
        <div class="level is-mobile">
          <div class="level-left">
            <div class="level-item">
              <div class="label is-tour">Tour</div>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div class="duration"><span class="icon-access_time"></span>{{ $t("time_title") }}{{ context.duration }}{{ $t("time_unit") }}</div>
            </div>
          </div>
        </div>
        <h2 class="title multiline" v-html="context.title"></h2>
        <div class="content">
          <stream-field
            :items="context.body"
          ></stream-field>
          <div v-if="context.preview" class="tex-preview">
            {{context.preview_text}}
          </div>
        </div>
        <div class="utilities">
          <div class="utility-item">
            <share-button
              :title="context.title"
              :description="context.summary"
              :quote="context.summary"
              hashtags="国立新美術館,NACT,NACTconic"
            >
            </share-button>
          </div>
          <div class="utility-item">
            <text-size-selector
              :isopen=false
              :haslabel=false
              class="is-align-right is-small"
            >
            </text-size-selector>
          </div>
        </div>
      </template>
      <template v-slot:spots>
        <slide v-for="tour_spot in context.tour_spots" :key="tour_spot.id" data-scrollbar>
          <section class="detail-spot">
            <div class="card has-detail">
              <hero
                v-if="tour_spot.spot_image || tour_spot.spot_caption"
                :items="[]"
                :eyecatch="tour_spot.spot_image.url"
                :caption="tour_spot.spot_captixon"
              >
              </hero>

              <div class="card-content">
                <header class="card-header">
                  <div class="title-wrapper">
                    <span v-if="tour_spot.spot" class="genre">SPOT DETAIL</span>
                    <span v-else class="genre">Notice</span>
                    <h2 class="title">{{tour_spot.spot_title}}</h2>
                  </div>
                  <audio-player
                    v-if="tour_spot.spot_audio || tour_spot.additional_audio"
                    :playlist="addToPlayList([tour_spot.additional_audio, tour_spot.spot_audio])"
                  >
                  </audio-player>
                </header>

                <ul v-if="tour_spot.features.length > 0" class="features content">
                  <li v-for="feature in tour_spot.features" :key="feature.id" v-html="feature.value.feature"></li>
                </ul>

                <div class="content">
                  <stream-field
                    :items="[...(tour_spot.additional_text ?? []), ...(tour_spot.spot_body ?? [])]"
                  ></stream-field>
                </div>
                <div class="utilities">
                  <div class="utility-item">
                    <share-button
                      :title="context.title"
                      :description="context.summary"
                      :quote="context.summary"
                      hashtags="国立新美術館,NACT,NACTconic"
                    >
                    </share-button>
                  </div>
                  <div class="utility-item">
                    <text-size-selector
                      :isopen=true
                      :haslabel=false
                      class="is-align-right"
                    >
                    </text-size-selector>
                  </div>
                </div>


                <floor-map
                  v-if="tour_spot.maps"
                  :items="tour_spot.maps"
                  :map_id=Number(tour_spot.id)
                >
                </floor-map>
                <floor-map
                  v-if="tour_spot.spot_maps"
                  :items="tour_spot.spot_maps"
                  :map_id=Number(tour_spot.id)
                >
                </floor-map>

                <div v-if="tour_spot.spot_information" class="information">
                  <stream-field
                    :items="tour_spot.spot_information"
                  ></stream-field>
                </div>
              </div>

            </div>

          </section>

          <div v-if="tour_spot.spot_gallery" class="image-gallery">
            <div class="container">
              <header class="aside-header">
                <h3 class="subtitle has-text-left">Photos</h3>
              </header>
              <masonry
                :items="tour_spot.spot_gallery"
                :spot_id=Number(tour_spot.id)
              >
              </masonry>
            </div>
          </div>

        </slide>

      </template>
    </tour>
  </section>
</template>

<script>
// @ is an alias to /src
import { mapGetters, mapActions } from 'vuex'
import { Slide } from 'hooper'
import Tour from '../components/Tour.vue'
import Hero from '../components/Hero.vue'
import AudioPlayer from '../components/AudioPlayer.vue'
import TextSizeSelector from '../components/TextSizeSelector.vue'
import StreamField from '../components/StreamField.vue'
import FloorMap from '../components/FloorMap.vue'
import Masonry from '../components/Masonry.vue'
import ShareButton from '../components/ShareButton.vue'

export default {
  components: {
    Tour,
    Hero,
    Slide,
    AudioPlayer,
    TextSizeSelector,
    StreamField,
    FloorMap,
    Masonry,
    ShareButton,
  },
  data() {
    return {
      baseUrl: process.env.VUE_APP_BASE_URL,
      type: 'tour.TourPage',
      slug: this.$route.params.slug,
      loadingText: 'Loading',
      guide: {
        steps: [
          {
            target: '.icon-question-circle',
            content: 'guide_button',
            offset: -100,
            duration: 800,
          },
          {
            target: '.bm-burger-button',
            content: 'guide_menu',
            offset: -100,
            duration: 800,
          },
          {
            target: '.tour-intro .button-start',
            content: 'guide_start',
            offset: -250,
            duration: 800,
            params: {
              placement: 'top',
            }
          },
          {
            target: '.spot-number',
            content: 'guide_step',
            offset: -100,
            duration: 800,
            params: {
              placement: 'bottom',
            }
          },
          {
            target: '.icon-keyboard_arrow_right',
            content: 'guide_swipe',
            offset: -1800,
            duration: 800,
            params: {
              placement: 'left',
            }
          },
          {
            target: '.hooper-slide.is-current .audioPlayer',
            content: 'guide_audio',
            offset: -300,
            duration: 800,
            params: {
              placement: 'bottom',
            }
          },
          {
            target: '.fab',
            content: 'guide_footprint',
            offset: -1800,
            duration: 800,
            params: {
              placement: 'bottom',
            }
          },
          {
            target: '.hooper-slide.is-current .text-size-selector.is-align-right',
            content: 'guide_textsize',
            offset: -250,
            duration: 800,
            params: {
              placement: 'top',
            }
          },
          {
            target: '.hooper-slide.is-current .image-gallery .img-wrap',
            content: 'guide_gallery',
            offset: -400,
            duration: 800,
            params: {
              placement: 'bottom',
            }
          },
        ]
      }
    }
  },
  computed: {
    ...mapGetters([
      'getAllPages',
      'getSingleContext',
      'isPagesLoaded',
    ]),
    context: function() {
      return this.getSingleContext(this.$route.params.slug)
    },
    spot_list() {
      let list = []
      for (let index = 0; index < this.context.tour_spots.length; index++) {
        const spot = this.context.tour_spots[index]
        list.push(
          {
              num: index + 1,
              title: spot.spot_title,
          }
        )
      }
      return list
    }
  },
  watch: {
    $route (){
      this.getDataFromApi()
    },
  },
  methods: {
    ...mapActions([
      'fetchPageByPath',
      'fetchPageByType',
      'setGuideSteps',
    ]),
    getDataFromApi: function() {
      this.fetchPageByType({ type: this.type, slug: this.$route.params.slug, lang:this.$root.$i18n.locale })
    },
    addToPlayList: function(audioArray) {
      let playlist = []
      for (const audio of audioArray) {
        if(audio != null){
          playlist.push(
            {
              title: audio.title,
              artist: '',
              url: this.baseUrl + audio.file,
              duration: audio.duration
            }
          )
        }
      }
      return playlist
    },
  },
  created() {},
  beforeMount() {
    this.getDataFromApi()
  },
  mounted() {
    this.setGuideSteps({ steps: this.guide.steps })
  }
}
</script>
